import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import { BRAND_CODES } from 'brand-config';
import {
  selectOfferBillingType,
  selectOfferCode,
  selectOfferCustomerBilled,
  selectOfferCustomerPrice,
  selectOfferGroupName,
  selectOfferPlanCode,
  selectOfferPrice,
  selectOfferTermEndDate,
  selectOfferTrialTerms,
} from 'services/offer/offerSelectors';
import { selectBrandCode } from 'services/brand/brandSelectors';
import { BILLING_TYPES, IDDEFENSE_DISCOUNT_OFFER_CODES } from './config';
import { resolveOfferSummaryCopy, resolvePlanName } from './utils';

const selectEnrollmentState = (state: RootState) => state.enrollment;

export const selectUseCode = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.useCode,
);

export const selectAccount = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.account,
);

export const selectEnrollmentOfferInfo = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.offer,
);

/* Flips atomically with the setOfferInfo write — gates the mount fetch. */
export const selectHasEnrollmentOfferInfoLoaded = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => !!enrollmentState.offer.planName,
);

/**
 * The same gate, but only for the offer that was actually asked for. The enrollment slice is
 * sessionStorage-persisted, so a bare presence check lets a second enrollment link opened in the
 * same tab skip the lookup entirely and inherit the first offer's plan name, price and billing
 * terms — the member then enrols against an offer that is not the one in their URL. Same
 * reasoning as `hasParticipantLoaded` on the five-step page.
 */
export const selectHasEnrollmentOfferInfoLoadedFor = createSelector(
  [
    selectEnrollmentOfferInfo,
    (_state: RootState, offerCode: string | null) => offerCode,
    (_state: RootState, _offerCode: string | null, planCode: string | null) =>
      planCode,
  ],
  (offerInfo, offerCode, planCode) =>
    !!offerInfo.planName &&
    !!offerCode &&
    !!planCode &&
    offerInfo.offerCode === offerCode &&
    offerInfo.planCode === planCode,
);

export const selectEnrollmentToken = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.token,
);

/**
 * Every step after the account POST authenticates with this token, so it doubles as "has the
 * account been created" — the five-step page uses it to reject a deep-linked `?step=`.
 */
export const selectHasEnrollmentToken = createSelector(
  [selectEnrollmentToken],
  (token) => !!token,
);

export const selectAgreedToPromotions = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.agreedToPromotions,
);

export const selectShowEmailOptIn = createSelector(
  [selectOfferBillingType, selectBrandCode],
  (billingType, brandCode) =>
    billingType?.toLowerCase() !== 'company billed' &&
    brandCode !== BRAND_CODES.idguard,
);

export const selectPolicyVerify = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.policyVerify,
);

export const selectAnpacVerifyAttempts = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.anpacVerifyAttempts ?? 0,
);

export const selectParticipant = createSelector(
  [selectEnrollmentState],
  (enrollmentState) => enrollmentState.participant,
);

/**
 * The token the stored participant was resolved from, and so the fetch-once gate for the
 * `?participantToken=` lookup (CLAUDE.md rule 8): `null` until the call has answered, and
 * compared against the token in the URL rather than merely tested for presence.
 *
 * A bare "has it loaded" boolean is not enough here. The enrollment slice is
 * sessionStorage-persisted, so a second invitation opened in the same session would otherwise
 * satisfy the gate with the *first* invitee's participant and enrol against their identity.
 */
export const selectParticipantToken = createSelector(
  [selectParticipant],
  (participant) => participant?.token ?? null,
);

export const selectParticipantId = createSelector(
  [selectParticipant],
  (participant) => participant?.participantId ?? null,
);

/**
 * The address the invitation was issued to, which seeds the account form.
 *
 * identityWeb reaches this the long way round — the reducer copies `participant.emailAddress` onto
 * `accountInfo`, and the account screen patches its whole form group from that
 * (enrollment.reducer.ts:70-73, basic-info.component.ts:95). Here it stays on the participant,
 * because `enrollment.account` means "the credentials this session created" and is what disables
 * the field.
 */
export const selectParticipantEmail = createSelector(
  [selectParticipant],
  (participant) => participant?.emailAddress ?? null,
);

/* The offer an invitation was issued against — the five-step flow's fallback when the URL
   carries no `offer_code`/`plan_code` (identityWeb enrollment.reducer.ts:77-78). */
export const selectParticipantOfferCode = createSelector(
  [selectParticipant],
  (participant) => participant?.offerCode ?? null,
);

export const selectParticipantPlanCode = createSelector(
  [selectParticipant],
  (participant) => participant?.planCode ?? null,
);

export const selectPrimaryUserName = createSelector(
  [selectParticipant],
  (participant) => participant?.primaryUserName ?? null,
);

export const selectIsAgedOutUser = createSelector(
  [selectParticipant],
  (participant) => !!participant?.agedOutUserId,
);

export const selectIsFamilyPlan = createSelector(
  [selectParticipant],
  (participant) => !!participant?.planFamily,
);

/**
 * A secondary enrollee on someone else's family plan: they are invited (`participantId`) onto
 * a plan with no adult allowance of its own, which is how identityWeb decides to show the
 * primary member's address read-only instead of collecting one
 * (enrollment-address-details.component.ts).
 */
export const selectIsSecondaryUser = createSelector(
  [selectParticipant],
  (participant) => !!participant?.participantId && !participant?.planMaxAdults,
);

/**
 * The account screen's invitation line, or `null` when this is not a family invitation.
 *
 * identityWeb swaps the screen's sub-heading for this whenever an invited enrollee is on a family
 * plan (basic-info.component.html:8-10, `enrollment:accountInfo.secondaryEnrollmentText`). Its
 * `plan$` is gated on a participant existing, which `selectIsFamilyPlan` already implies.
 *
 * The plan name goes through `resolvePlanName` even though identityWeb prints the raw name here —
 * its own id badge applies the override map (id-badge.component.ts:130-135), and quoting two
 * different names for one plan across two screens of the same flow reads as a defect.
 */
export const selectFamilyInviteCopy = createSelector(
  [selectIsFamilyPlan, selectPrimaryUserName, selectParticipant],
  (isFamilyPlan, primaryUserName, participant) => {
    const planName = resolvePlanName(
      participant?.planCode,
      participant?.planName,
    );
    if (!isFamilyPlan || !planName) {
      return null;
    }
    const inviterName = [primaryUserName?.first, primaryUserName?.last]
      .filter(Boolean)
      .join(' ');
    if (!inviterName) {
      return null;
    }
    return `${inviterName} has invited you to enroll in their ${planName}`;
  },
);

export const selectIsComplimentaryOffer = createSelector(
  [selectOfferBillingType],
  (billingType) => billingType?.toLowerCase() === BILLING_TYPES.complimentary,
);

export const selectIsCompanyEnrollment = createSelector(
  [selectOfferBillingType],
  (billingType) => billingType?.toLowerCase() === BILLING_TYPES.companyBilled,
);

/**
 * Whether this member pays for the membership themselves — the only case that collects a card.
 *
 * identityWeb gates the Zuora mount point on `!isFamilyPlan && offer.customerBilled`
 * (enrollment-billing-details.component.html:89-100): a family-plan invitee is not the payer, and
 * an employer-paid, payroll-deducted or company-billed offer never charges one. Either way the
 * billing step shows its explanation and a single Go-to-dashboard button, and no
 * `enrollmentStep=billingInfo` PATCH is ever made (`proceedWithoutSubmitting`).
 *
 * Complimentary is spelled out rather than left to `customerBilled` so the branch does not depend
 * on how the marketing API happens to flag a free offer.
 */
export const selectShouldCollectPayment = createSelector(
  [selectIsComplimentaryOffer, selectOfferCustomerBilled, selectIsFamilyPlan],
  (isComplimentary, customerBilled, isFamilyPlan) =>
    !isComplimentary && !!customerBilled && !isFamilyPlan,
);

/**
 * A complimentary offer with no expiry date never reaches a summary screen — identityWeb drops
 * the step and sends the member straight from personal-info to the dashboard loader
 * (`skipBilling`, enrollment-personal-details.component.ts:95-97).
 */
export const selectShouldSkipPlanSummary = createSelector(
  [selectIsComplimentaryOffer, selectOfferTermEndDate],
  (isComplimentary, termEndDate) =>
    isComplimentary && (!termEndDate || termEndDate === 'N/A'),
);

/**
 * The price the summary screens quote, in cents.
 *
 * `eb buy up cc billed` bills the member only the buy-up portion, so it quotes `customerPrice`;
 * every other billing type quotes the full plan `price`
 * (enrollment-plan-summary.component.html:65, :76). Note this deliberately differs from
 * `selectOfferPlanCardPriceCents`, which prefers `customerPrice` whenever it is set.
 */
export const selectSummaryPriceCents = createSelector(
  [selectOfferBillingType, selectOfferPrice, selectOfferCustomerPrice],
  (billingType, price, customerPrice) => {
    if (billingType?.trim().toLowerCase() === BILLING_TYPES.ebBuyUpCcBilled) {
      return customerPrice ?? price ?? 0;
    }
    return price ?? 0;
  },
);

/**
 * Everything the plan-summary screen renders for the current offer, already resolved.
 *
 * Assembling the resolver's input here keeps the screen free of the eight-way `billingType`
 * branch identityWeb spreads across its template, and means the page reads one selector instead
 * of ten (CLAUDE.md rule 9).
 */
export const selectOfferSummaryCopy = createSelector(
  [
    selectOfferBillingType,
    selectOfferPlanCode,
    selectEnrollmentOfferInfo,
    selectOfferGroupName,
    selectOfferTermEndDate,
    selectIsFamilyPlan,
    selectPrimaryUserName,
    selectOfferTrialTerms,
    selectSummaryPriceCents,
  ],
  (
    billingType,
    planCode,
    offerInfo,
    groupName,
    termEndDate,
    isFamilyPlan,
    primaryUserName,
    trialTerms,
    priceCents,
  ) =>
    resolveOfferSummaryCopy({
      billingType,
      planName: resolvePlanName(planCode, offerInfo.planName),
      groupName,
      termDescription: offerInfo.termDescription,
      termName: offerInfo.termName,
      termEndDate,
      isFamilyPlan,
      primaryUserName,
      priceCents,
      ...trialTerms,
    }),
);

/**
 * identityWeb swaps the auto-renew consent for discounted first-year wording on three specific
 * Identity Defense offers (enrollment-plan-summary.component.ts:89-97).
 */
export const selectIsIddefenseDiscountOffer = createSelector(
  [selectBrandCode, selectOfferCode],
  (brandCode, offerCode) =>
    brandCode === BRAND_CODES.identityDefense &&
    !!offerCode &&
    IDDEFENSE_DISCOUNT_OFFER_CODES.includes(offerCode.toLowerCase()),
);
